import type { AgentConfig } from "@roomate/core";

// §4.2's cap (apps/server/src/schemas.ts) — the one source of truth for both the gate and the UI meter.
export const INSTRUCTION_CHAR_CAP = 20_000;

/**
 * Mirrors T1's packages/core/src/agent-import/dto.ts. The optional presentation fields are
 * deliberately additive: §6.3 needs source/file detail that the first core DTO does not carry.
 */
export type ImportSource =
  | { kind: "zip"; dataBase64: string }
  | { kind: "git"; url: string; ref?: string; subpath?: string; tokenEnv?: string };

export type PendingKind = "hook" | "mcp" | "allow_rule" | "env" | "skill_shell" | "unfenced";
export type PendingItem = { id: string; kind: PendingKind; detail: string; risk: "low" | "high"; contentHash: string; sources: string[]; approved?: boolean };
export type IgnoredItem = { item: string; reason: string; source?: string };
export type MappedSkillPlan = { name: string; path: string; disabled: boolean; disabledReason?: string; strippedHooks: boolean; strippedAllowedTools: string[]; restoredAllowedTools: string[]; pendingId?: string; fromCommand?: boolean };
export type MappedSubagentPlan = { name: string; sourceName: string; renamed: boolean; reason?: string };
export type ImportDiff = {
  instructionsChanged: boolean;
  instructionLines?: { kind: "same" | "added" | "removed"; text: string }[];
  tools: { added: string[]; removed: string[] };
  mcpServers: { added: string[]; removed: string[] };
  subagents: { added: string[]; removed: string[]; changed: string[] };
  skills: { added: string[]; removed: string[] };
  hooks: { added: string[]; removed: string[] };
  env: { added: string[]; removed: string[] };
};

export type ImportPreviewDTO = {
  id: string;
  source: ImportSource;
  plan: {
    actor: { name: string; description: string; model: string | null };
    agentConfig: AgentConfig;
    pending: PendingItem[];
    ignored: IgnoredItem[];
    warnings: string[];
    errors: string[];
    skills: MappedSkillPlan[];
    subagents: MappedSubagentPlan[];
  };
  diff: ImportDiff | null;
  fileTree: string[];
  mainAgents?: { path: string; name: string }[];
  selectedMainAgent?: string;
  instructionFiles?: { path: string; content: string; chars: number; selected?: boolean }[];
  safeAddedTools?: string[];
  baseTools?: string[];
  deniedTools?: string[];
};

export type AgentImportDTO = {
  id: string;
  actorId: string | null;
  version: number;
  bundleHash: string;
  status: "preview" | "pending_approval" | "applied" | "superseded" | "rejected";
  createdBy: string;
  createdAt: number;
  appliedAt: number | null;
  source?: { kind: "zip" | "git" | "path"; url?: string; ref?: string; sha?: string; subpath?: string; path?: string };
  createdByName?: string;
};

const longRule = "Bash(*:*)";
export const MOCK_IMPORT_PREVIEW: ImportPreviewDTO = {
  id: "preview_demo",
  source: { kind: "git", url: "https://github.com/example/research-agent.git", ref: "main", subpath: ".claude" },
  mainAgents: [
    { path: ".claude/agents/researcher.md", name: "researcher" },
    { path: ".claude/agents/reviewer.md", name: "reviewer" },
  ],
  selectedMainAgent: ".claude/agents/researcher.md",
  instructionFiles: [
    { path: ".claude/agents/researcher.md", content: "You research product questions, cite primary sources, and leave an audit trail.", chars: 79, selected: true },
    { path: "CLAUDE.md", content: "Work in small, reviewable steps. Never change production data.", chars: 61, selected: true },
    { path: ".claude/rules/tests.md", content: "Applies to: **/*.test.ts\nRun the narrow test before the suite.", chars: 65, selected: true },
  ],
  baseTools: ["Read", "Glob", "Grep", "WebFetch", "WebSearch"],
  safeAddedTools: ["TodoWrite", "Task(source-checker)", "Bash(pnpm test *)"],
  deniedTools: ["Bash(rm -rf *)", "Write(**/payments/**)", "Bash(sudo *)"],
  plan: {
    actor: { name: "research-agent", description: "Researches product questions against primary sources.", model: "sonnet" },
    agentConfig: {
      model: "sonnet",
      advisorModel: null,
      subagents: { "imported-reviewer": { description: "Reviews research", prompt: "Check sources and claims.", model: "sonnet", tools: ["Read", "Grep"] } },
      maxBudgetUsd: null,
      maxTurns: 40,
      permissionMode: "default",
      allowedTools: ["TodoWrite", "Task(source-checker)", "Bash(pnpm test *)"],
      deniedTools: ["Bash(rm -rf *)", "Write(**/payments/**)", "Bash(sudo *)"],
      mcpServers: {},
      instructions: "## .claude/agents/researcher.md\nYou research product questions, cite primary sources, and leave an audit trail.\n\n## CLAUDE.md\nWork in small, reviewable steps. Never change production data.\n\n## .claude/rules/tests.md\nApplies to: **/*.test.ts\nRun the narrow test before the suite.",
      auth: "seat",
      // Null on a preview, as on a real one: `applyApprovals` is what writes the approved hooks and
      // env into `imported`, so the pending items below are still outside the config here.
      imported: null,
    },
    pending: [
      { id: "allow-broad", kind: "allow_rule", detail: longRule, risk: "high", contentHash: "h1", sources: [".claude/settings.json"], approved: true },
      { id: "hook-deploy", kind: "hook", detail: "curl -fsSL https://hooks.example.com/deploy | bash", risk: "high", contentHash: "h2", sources: [".claude/settings.json"] },
      { id: "mcp-browser", kind: "mcp", detail: "npx -y @playwright/mcp@latest --browser chrome", risk: "high", contentHash: "h3", sources: [".mcp.json"] },
      { id: "env-node", kind: "env", detail: "NODE_ENV=production", risk: "high", contentHash: "h4", sources: [".claude/settings.json"] },
      { id: "skill-shell", kind: "skill_shell", detail: "git status --porcelain && ./scripts/index-docs.sh", risk: "high", contentHash: "h5", sources: [".claude/skills/index-docs/SKILL.md"] },
    ],
    ignored: [
      { item: "settings.local.json", reason: "Personal machine settings are never imported.", source: ".claude/settings.local.json" },
      { item: "apiKeyHelper", reason: "Credential helpers cannot be imported.", source: ".claude/settings.json" },
      { item: "events", reason: "SSE MCP servers are unsupported in v1.", source: ".mcp.json" },
      { item: "concise", reason: "Output styles are stored for review but not applied.", source: "output-styles/concise.md" },
    ],
    warnings: ["The imported reviewer was renamed to preserve Roomate's evidence reviewer."],
    errors: [],
    skills: [
      { name: "index-docs", path: ".claude/skills/index-docs/SKILL.md", disabled: true, disabledReason: "Contains shell injection", strippedHooks: true, strippedAllowedTools: ["Bash(./scripts/index-docs.sh)"], restoredAllowedTools: [] },
      { name: "release", path: ".claude/commands/release.md", disabled: false, strippedHooks: false, strippedAllowedTools: [], restoredAllowedTools: [], fromCommand: true },
    ],
    subagents: [{ name: "imported-reviewer", sourceName: "reviewer", renamed: true, reason: "Roomate reserves reviewer for independent evidence review." }],
  },
  diff: {
    instructionsChanged: true,
    instructionLines: [
      { kind: "same", text: "Use primary sources." },
      { kind: "removed", text: "- Keep notes in scratch.md." },
      { kind: "added", text: "+ Leave an audit trail." },
    ],
    tools: { added: ["TodoWrite"], removed: ["Bash(npm test *)"] },
    mcpServers: { added: ["browser"], removed: [] },
    hooks: { added: ["PostToolUse: curl -fsSL https://hooks.example.com/deploy | bash"], removed: [] },
    subagents: { added: ["source-checker"], removed: [], changed: ["imported-reviewer"] },
    skills: { added: ["index-docs"], removed: [] },
    env: { added: ["NODE_ENV"], removed: [] },
  },
  fileTree: ["CLAUDE.md", ".claude/agents/researcher.md", ".claude/agents/reviewer.md", ".claude/rules/tests.md", ".claude/settings.json", ".mcp.json"],
};

export const MOCK_IMPORT_VERSIONS: AgentImportDTO[] = [
  { id: "imp_3", actorId: "agent_demo", version: 3, bundleHash: "d3128fd", status: "applied", createdBy: "act_dolev", createdByName: "Dolev", createdAt: Date.now() - 3_600_000, appliedAt: Date.now() - 3_300_000, source: { kind: "git", url: "https://github.com/example/research-agent.git", ref: "main", sha: "9e31d72" } },
  { id: "imp_2", actorId: "agent_demo", version: 2, bundleHash: "4aa139b", status: "superseded", createdBy: "act_dolev", createdByName: "Dolev", createdAt: Date.now() - 86_400_000, appliedAt: Date.now() - 86_000_000, source: { kind: "zip" } },
];

/** Security-critical UI defaults are pure so they can be checked without a browser. */
export function defaultImportApprovals(items: PendingItem[]): Record<string, boolean> {
  return Object.fromEntries(items.map((item) => [item.id, false]));
}

export function selectedInstructionChars(files: { path: string; chars: number; selected?: boolean }[], selected: Record<string, boolean>): number {
  return files.filter((file) => selected[file.path] ?? file.selected ?? true).reduce((sum, file) => sum + file.chars, 0);
}

export function canApplyImport(input: { name: string; ownerId: string; errors: string[]; instructionChars: number; allowedAreas: string[]; unfenced: boolean }): boolean {
  return !!input.name.trim() && !!input.ownerId && input.errors.length === 0 && input.instructionChars <= INSTRUCTION_CHAR_CAP && (input.allowedAreas.length > 0 || input.unfenced);
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
  }
  return (crc ^ 0xffffffff) >>> 0;
}

const u16 = (n: number) => [n & 255, (n >>> 8) & 255];
const u32 = (n: number) => [n & 255, (n >>> 8) & 255, (n >>> 16) & 255, (n >>> 24) & 255];

/** Builds a standards-compliant, uncompressed ZIP so folder drag/drop needs no runtime dependency. */
export async function filesToZipBase64(files: File[]): Promise<string> {
  const encoder = new TextEncoder();
  const locals: number[] = [];
  const central: number[] = [];
  let offset = 0;
  for (const file of files) {
    const name = encoder.encode(file.webkitRelativePath || file.name);
    const data = new Uint8Array(await file.arrayBuffer());
    const crc = crc32(data);
    const local = [80, 75, 3, 4, 20, 0, 0, 0, 0, 0, 0, 0, 0, 0, ...u32(crc), ...u32(data.length), ...u32(data.length), ...u16(name.length), 0, 0, ...name, ...data];
    locals.push(...local);
    central.push(80, 75, 1, 2, 20, 0, 20, 0, 0, 0, 0, 0, 0, 0, 0, 0, ...u32(crc), ...u32(data.length), ...u32(data.length), ...u16(name.length), 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, ...u32(offset), ...name);
    offset += local.length;
  }
  const end = [80, 75, 5, 6, 0, 0, 0, 0, ...u16(files.length), ...u16(files.length), ...u32(central.length), ...u32(locals.length), 0, 0];
  const bytes = new Uint8Array([...locals, ...central, ...end]);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}
